import { useCallback, useState } from 'react';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

interface UseFormStateOptions<T extends Record<string, string>> {
  initialValues: T;
  validate: (values: T) => Partial<Record<keyof T, string>>;
  endpoint: string;
}

export function useFormState<T extends Record<string, string>>(
  { initialValues, validate, endpoint }: UseFormStateOptions<T>,
  open: boolean,
  source?: string,
) {
  const [formState, setFormState] = useState<FormState>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [values, setValues] = useState<T>(initialValues);
  const [wasOpen, setWasOpen] = useState(open);

  if (open && !wasOpen) {
    setWasOpen(true);
    setFormState('idle');
    setErrors({});
    setValues(initialValues);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  const handleChange = useCallback((field: keyof T, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: undefined };
    });
  }, []);

  const clearField = useCallback((field: keyof T) => {
    setValues(prev => ({ ...prev, [field]: '' }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: undefined };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: { preventDefault: () => void }) => {
      e.preventDefault();

      const fieldErrors = validate(values);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }

      setErrors({});
      setFormState('submitting');

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...values, source }),
        });

        if (!res.ok) throw new Error('Failed to submit');

        setFormState('success');
      } catch {
        setFormState('error');
      }
    },
    [values, validate, endpoint, source],
  );

  return { formState, errors, values, handleChange, clearField, handleSubmit };
}
